import {
    type CSSProperties,
    useEffect,
    Fragment,
    useState,
    useRef
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type IBottomSheetProps,
    NCoreUIKitBottomSheet,
    type IBottomSheetRef,
    NCoreUIKitTheme,
    BottomSheet,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "BottomSheet";

const USAGE_CODE = `const [isOpen, setIsOpen] = useState(false);

<BottomSheet
    renderBottom={() => <Button
        onPress={() => setIsOpen(false)}
        spreadBehaviour="stretch"
        title="Save"
        size="large"
    />}
    description="Choose where you want to hear from us."
    onOpenChange={setIsOpen}
    title="Notifications"
    isWorkWithAnimation={true}
    isOpen={isOpen}
>
    <Switch
        onCheckedChange={setIsEmailOn}
        spreadBehaviour="stretch"
        isChecked={isEmailOn}
        title="Email"
    />
</BottomSheet>`;

const HEIGHT_CODE = `<BottomSheet
    snapPoint={320}
/>

<BottomSheet
    isCanFullScreenOnSwipe={true}
    snapPoint={320}
/>

<BottomSheet
    isWorkAsFullScreen={true}
/>`;

const IMPERATIVE_CODE = `const sheetRef = useRef<IBottomSheetRef>(null);

<BottomSheet
    title="Share"
    ref={sheetRef}
/>

sheetRef.current?.open();

sheetRef.current?.close();

NCoreUIKitBottomSheet.open({
    children: <ShareOptions/>,
    title: "Share"
});`;

const REPORT_DELAY = 1500;

const LINES_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex"
};

const FOOTER_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex"
};

type ChannelKey = "email" | "push" | "sms";

type ChannelType = {
    titleKey: string;
    hintKey: string;
    key: ChannelKey;
};

type HeightSampleType = Pick<IBottomSheetProps, "isCanFullScreenOnSwipe" | "isWorkAsFullScreen"> & {
    isFixedHeight?: boolean;
    descriptionKey: string;
    lineCount: number;
    labelKey: string;
};

const CHANNELS: Array<ChannelType> = [
    {
        hintKey: "bottomSheetPage.channelEmailHint",
        titleKey: "bottomSheetPage.channelEmail",
        key: "email"
    },
    {
        hintKey: "bottomSheetPage.channelPushHint",
        titleKey: "bottomSheetPage.channelPush",
        key: "push"
    },
    {
        hintKey: "bottomSheetPage.channelSmsHint",
        titleKey: "bottomSheetPage.channelSms",
        key: "sms"
    }
];

const INITIAL_CHANNELS: Record<ChannelKey, boolean> = {
    email: true,
    push: true,
    sms: false
};

const ACCOUNT_ITEMS = [
    "sample.item1",
    "sample.item2",
    "sample.item3"
];

const HEIGHT_SAMPLES: Array<HeightSampleType> = [
    {
        descriptionKey: "bottomSheetPage.heightFitDescription",
        labelKey: "bottomSheetPage.heightFit",
        lineCount: 3
    },
    {
        descriptionKey: "bottomSheetPage.heightFixedDescription",
        labelKey: "bottomSheetPage.heightFixed",
        isFixedHeight: true,
        lineCount: 12
    },
    {
        descriptionKey: "bottomSheetPage.heightExpandableDescription",
        labelKey: "bottomSheetPage.heightExpandable",
        isCanFullScreenOnSwipe: true,
        isFixedHeight: true,
        lineCount: 12
    },
    {
        descriptionKey: "bottomSheetPage.heightFullDescription",
        labelKey: "bottomSheetPage.heightFull",
        isWorkAsFullScreen: true,
        lineCount: 12
    }
];

const HeightShowcase = ({
    sample
}: {
    sample: HeightSampleType;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    return <Fragment>
        <Button
            onPress={() => setIsOpen(true)}
            title={t(sample.labelKey)}
            variant="outline"
            type="neutral"
        />
        <BottomSheet
            snapPoint={sample.isFixedHeight ? shapeConfigs.chartHeight.large : undefined}
            isCanFullScreenOnSwipe={sample.isCanFullScreenOnSwipe}
            isWorkAsFullScreen={sample.isWorkAsFullScreen}
            description={t(sample.descriptionKey)}
            title={t(sample.labelKey)}
            isWorkWithAnimation={true}
            onOpenChange={setIsOpen}
            isOpen={isOpen}
        >
            <div
                style={{
                    ...LINES_STYLE,
                    gap: spaces.spacingMd
                }}
            >
                {
                    Array.from({
                        length: sample.lineCount
                    }, (_, index) => <Text
                        variant="bodyMediumSize"
                        color="high"
                        key={index}
                    >
                        {t("bottomSheetPage.line", [
                            index + 1
                        ])}
                    </Text>)
                }
            </div>
        </BottomSheet>
    </Fragment>;
};

const LoadingShowcase = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const [
        isReady,
        setIsReady
    ] = useState(false);

    useEffect(() => {
        if(!isOpen) {
            return;
        }

        const timer = window.setTimeout(() => setIsReady(true), REPORT_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isOpen
    ]);

    const handleOpen = () => {
        setIsReady(false);

        setIsOpen(true);
    };

    return <Fragment>
        <Button
            title={t("bottomSheetPage.openReport")}
            onPress={handleOpen}
            variant="outline"
            type="neutral"
        />
        <BottomSheet
            description={t("bottomSheetPage.reportDescription")}
            title={t("bottomSheetPage.reportTitle")}
            isWorkWithAnimation={true}
            isContentReady={isReady}
            onOpenChange={setIsOpen}
            isOpen={isOpen}
        >
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {t("bottomSheetPage.reportContent")}
            </Text>
        </BottomSheet>
    </Fragment>;
};

const LockedShowcase = () => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const handleClose = () => setIsOpen(false);

    const renderFooter = () => <div
        style={{
            ...FOOTER_STYLE,
            gap: spaces.spacingSm
        }}
    >
        <Button
            title={t("bottomSheetPage.discard")}
            spreadBehaviour="stretch"
            onPress={handleClose}
            type="danger"
            size="large"
        />
        <Button
            title={t("bottomSheetPage.keepEditing")}
            spreadBehaviour="stretch"
            onPress={handleClose}
            variant="outline"
            type="neutral"
            size="large"
        />
    </div>;

    return <Fragment>
        <Button
            title={t("bottomSheetPage.openLocked")}
            onPress={() => setIsOpen(true)}
            variant="outline"
            type="neutral"
        />
        <BottomSheet
            description={t("bottomSheetPage.lockedDescription")}
            title={t("bottomSheetPage.lockedTitle")}
            renderBottom={renderFooter}
            isWorkWithAnimation={true}
            isCloseOnOverlay={false}
            onOpenChange={setIsOpen}
            isSwipeClose={false}
            isShowClose={false}
            isOpen={isOpen}
        >
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {t("bottomSheetPage.lockedBody")}
            </Text>
        </BottomSheet>
    </Fragment>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("bottomSheetPage.contentTitle"),
        id: "content"
    },
    {
        title: t("bottomSheetPage.heightTitle"),
        id: "height"
    },
    {
        title: t("bottomSheetPage.loadingTitle"),
        id: "loading"
    },
    {
        title: t("bottomSheetPage.dismissTitle"),
        id: "dismiss"
    },
    {
        title: t("bottomSheetPage.imperativeTitle"),
        id: "imperative"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const BottomSheetPage = () => {
    const {
        t
    } = useDocsI18n();

    const sheetRef = useRef<IBottomSheetRef>(null);

    const [
        isNotificationsOpen,
        setIsNotificationsOpen
    ] = useState(false);

    const [
        channels,
        setChannels
    ] = useState(INITIAL_CHANNELS);

    const [
        isAccountOpen,
        setIsAccountOpen
    ] = useState(false);

    const [
        isCustomOpen,
        setIsCustomOpen
    ] = useState(false);

    const channelCount = CHANNELS.filter((channel) => channels[channel.key]).length;

    const handleNotificationsClose = () => setIsNotificationsOpen(false);

    const handleAccountClose = () => setIsAccountOpen(false);

    const handleChannelToggle = (key: ChannelKey) => {
        setChannels((current) => ({
            ...current,
            [key]: !current[key]
        }));
    };

    const handleRefOpen = () => sheetRef.current?.open();

    const handleRefClose = () => sheetRef.current?.close();

    const handleCodeOpen = () => {
        NCoreUIKitBottomSheet.open({
            description: t("bottomSheetPage.codeDescription"),
            title: t("bottomSheetPage.codeTitle"),
            children: <Text
                variant="bodyMediumSize"
                color="high"
            >
                {t("bottomSheetPage.codeBody")}
            </Text>
        });
    };

    const renderNotificationsFooter = () => <Button
        onPress={handleNotificationsClose}
        title={t("sample.action")}
        spreadBehaviour="stretch"
        size="large"
    />;

    const renderCustomHeader = () => <Text
        variant="titleLargeSize"
        color="high"
    >
        {t("bottomSheetPage.customHeading")}
    </Text>;

    const renderRefFooter = () => <Button
        title={t("bottomSheetPage.close")}
        spreadBehaviour="stretch"
        onPress={handleRefClose}
        variant="outline"
        type="neutral"
        size="large"
    />;

    return <Article>
        <ComponentHeader
            lead={t("bottomSheetPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("bottomSheetPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("bottomSheetPage.overviewCaption")}
            >
                <Button
                    title={t("bottomSheetPage.openNotifications")}
                    onPress={() => setIsNotificationsOpen(true)}
                    variant="outline"
                    type="neutral"
                />
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("bottomSheetPage.channelsOn", [
                        channelCount
                    ])}
                </Text>
                <BottomSheet
                    description={t("bottomSheetPage.notificationsDescription")}
                    title={t("bottomSheetPage.notificationsTitle")}
                    renderBottom={renderNotificationsFooter}
                    onOpenChange={setIsNotificationsOpen}
                    isOpen={isNotificationsOpen}
                    isWorkWithAnimation={true}
                >
                    {
                        CHANNELS.map((channel) => <Switch
                            onPress={() => handleChannelToggle(channel.key)}
                            isChecked={channels[channel.key]}
                            subTitle={t(channel.hintKey)}
                            title={t(channel.titleKey)}
                            spreadBehaviour="stretch"
                            key={channel.key}
                        />)
                    }
                </BottomSheet>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("bottomSheetPage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("bottomSheetPage.contentText")}
            </Paragraph>
            <Preview>
                <Button
                    title={t("bottomSheetPage.openAccount")}
                    onPress={() => setIsAccountOpen(true)}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("bottomSheetPage.openCustomHeader")}
                    onPress={() => setIsCustomOpen(true)}
                    variant="outline"
                    type="neutral"
                />
                <BottomSheet
                    description={t("bottomSheetPage.accountDescription", [
                        t("sample.name")
                    ])}
                    title={t("bottomSheetPage.accountTitle")}
                    onOpenChange={setIsAccountOpen}
                    isWorkWithAnimation={true}
                    isOpen={isAccountOpen}
                    isCustomPadding={true}
                    isShowClose={false}
                >
                    {
                        ACCOUNT_ITEMS.map((itemKey) => <Button
                            onPress={handleAccountClose}
                            spreadBehaviour="stretch"
                            title={t(itemKey)}
                            variant="ghost"
                            type="neutral"
                            key={itemKey}
                            size="large"
                        />)
                    }
                </BottomSheet>
                <BottomSheet
                    modalProps={{
                        "aria-label": t("bottomSheetPage.customHeading")
                    }}
                    renderHeader={renderCustomHeader}
                    onOpenChange={setIsCustomOpen}
                    isWorkWithAnimation={true}
                    isOpen={isCustomOpen}
                    isShowClose={true}
                >
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("sample.body")}
                    </Text>
                </BottomSheet>
            </Preview>
        </Section>
        <Section
            title={t("bottomSheetPage.heightTitle")}
            id="height"
        >
            <Paragraph>
                {t("bottomSheetPage.heightText")}
            </Paragraph>
            <Preview
                caption={t("bottomSheetPage.heightCaption")}
            >
                {
                    HEIGHT_SAMPLES.map((sample) => <HeightShowcase
                        key={sample.labelKey}
                        sample={sample}
                    />)
                }
            </Preview>
            <CodeBlock
                code={HEIGHT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("bottomSheetPage.loadingTitle")}
            id="loading"
        >
            <Paragraph>
                {t("bottomSheetPage.loadingText")}
            </Paragraph>
            <Preview
                caption={t("bottomSheetPage.loadingCaption")}
            >
                <LoadingShowcase/>
            </Preview>
        </Section>
        <Section
            title={t("bottomSheetPage.dismissTitle")}
            id="dismiss"
        >
            <Paragraph>
                {t("bottomSheetPage.dismissText")}
            </Paragraph>
            <Preview
                caption={t("bottomSheetPage.dismissCaption")}
            >
                <LockedShowcase/>
            </Preview>
        </Section>
        <Section
            title={t("bottomSheetPage.imperativeTitle")}
            id="imperative"
        >
            <Paragraph>
                {t("bottomSheetPage.imperativeText")}
            </Paragraph>
            <Preview>
                <Button
                    title={t("bottomSheetPage.openWithRef")}
                    onPress={handleRefOpen}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("bottomSheetPage.openFromCode")}
                    onPress={handleCodeOpen}
                    variant="outline"
                    type="neutral"
                />
                <BottomSheet
                    description={t("bottomSheetPage.refDescription")}
                    title={t("bottomSheetPage.refTitle")}
                    renderBottom={renderRefFooter}
                    ref={sheetRef}
                >
                    <Text
                        variant="bodyMediumSize"
                        color="high"
                    >
                        {t("bottomSheetPage.refBody")}
                    </Text>
                </BottomSheet>
            </Preview>
            <CodeBlock
                code={IMPERATIVE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("bottomSheetPage.a11yRole"),
                    t("bottomSheetPage.a11yLabel"),
                    t("bottomSheetPage.a11yFocus"),
                    t("bottomSheetPage.a11yDismiss"),
                    t("bottomSheetPage.a11yClose"),
                    t("bottomSheetPage.a11yLoading"),
                    t("bottomSheetPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("bottomSheetPage.propIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("bottomSheetPage.propDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("bottomSheetPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("bottomSheetPage.propRenderHeader"),
                        type: "() => ReactNode",
                        name: "renderHeader"
                    },
                    {
                        description: t("bottomSheetPage.propRenderSubHeader"),
                        type: "() => ReactNode",
                        name: "renderSubHeader"
                    },
                    {
                        description: t("bottomSheetPage.propRenderBottom"),
                        type: "() => ReactNode",
                        name: "renderBottom"
                    },
                    {
                        description: t("bottomSheetPage.propIsShowClose"),
                        name: "isShowClose",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("bottomSheetPage.propOnOpened"),
                        type: "() => void",
                        name: "onOpened"
                    },
                    {
                        description: t("bottomSheetPage.propOnClosed"),
                        type: "({ id }) => void",
                        name: "onClosed"
                    },
                    {
                        description: t("bottomSheetPage.propIsContentReady"),
                        name: "isContentReady",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propSnapPoint"),
                        name: "snapPoint",
                        type: "number"
                    },
                    {
                        description: t("bottomSheetPage.propIsAutoHeight"),
                        defaultValue: "false",
                        name: "isAutoHeight",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propIsCanFullScreenOnSwipe"),
                        name: "isCanFullScreenOnSwipe",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propIsForceFullScreenOnSwipe"),
                        name: "isForceFullScreenOnSwipe",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propIsWorkAsFullScreen"),
                        name: "isWorkAsFullScreen",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propIsCloseOnOverlay"),
                        name: "isCloseOnOverlay",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propOnOverlayPressed"),
                        name: "onOverlayPressed",
                        type: "() => void"
                    },
                    {
                        description: t("bottomSheetPage.propIsSwipeClose"),
                        defaultValue: "true",
                        name: "isSwipeClose",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propIsCanSwipe"),
                        defaultValue: "true",
                        name: "isCanSwipe",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propIsShowHandle"),
                        defaultValue: "true",
                        name: "isShowHandle",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propIsWorkWithAnimation"),
                        name: "isWorkWithAnimation",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propIsCustomPadding"),
                        name: "isCustomPadding",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propBackgroundColor"),
                        type: "keyof ContainerContentColors",
                        defaultValue: "\"raised\"",
                        name: "backgroundColor"
                    },
                    {
                        description: t("bottomSheetPage.propHandleHeight"),
                        name: "handleHeight",
                        type: "number"
                    },
                    {
                        description: t("bottomSheetPage.propScrollEndThreshold"),
                        name: "scrollEndThreshold",
                        defaultValue: "0.85",
                        type: "number"
                    },
                    {
                        description: t("bottomSheetPage.propOnScrollEnd"),
                        name: "onScrollEnd",
                        type: "() => void"
                    },
                    {
                        description: t("bottomSheetPage.propIsWrapSafeAreaContext"),
                        name: "isWrapSafeAreaContext",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propIsWorkWithPortal"),
                        name: "isWorkWithPortal",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("bottomSheetPage.propPortalName"),
                        defaultValue: "\"bottomSheet-system\"",
                        name: "portalName",
                        type: "string"
                    },
                    {
                        description: t("bottomSheetPage.propModalProps"),
                        type: "Partial<IModalProps>",
                        name: "modalProps"
                    },
                    {
                        description: t("bottomSheetPage.propScrollViewProps"),
                        type: "ScrollViewProps",
                        name: "scrollViewProps"
                    },
                    {
                        description: t("bottomSheetPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("bottomSheetPage.propCustomKey"),
                        name: "customKey",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("bottomSheetPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("bottomSheetPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("bottomSheetPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default BottomSheetPage;
